import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Newspaper, Rocket, Smartphone } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

import { API } from "@/lib/api";

export const NEWS = [
  {
    date: "August 2026",
    tag: "Company",
    Icon: Globe,
    title: "Sunkev Enterprises launches its official corporate website",
    text: "Our new website is live — a single place to learn about Sunkev Enterprises Private Limited, our vision, and our flagship platform, Craftsi.",
  },
  {
    date: "2026",
    tag: "Platform",
    Icon: Rocket,
    title: "Craftsi enters active development",
    text: "Craftsi, our local services marketplace connecting customers with trusted businesses and service providers, is being built for launch.",
  },
  {
    date: "Coming Soon",
    tag: "Product",
    Icon: Smartphone,
    title: "Craftsi Customer and Vendor apps heading to app stores",
    text: "The Craftsi Customer App and Vendor App will be available on Google Play and the App Store. Watch this space for launch announcements.",
  },
];

const TAG_ICONS = { Company: Globe, Platform: Rocket, Product: Smartphone };

export const useNews = () => {
  const [items, setItems] = useState(NEWS);
  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((r) => {
        if (r.data.length) {
          setItems(r.data.map((n) => ({ ...n, Icon: TAG_ICONS[n.tag] || Newspaper })));
        }
      })
      .catch(() => {});
  }, []);
  return items;
};

const NewsSection = () => {
  const news = useNews();
  return (
  <section data-testid="news-section" className="bg-mist py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading
          eyebrow="News & Updates"
          title="The latest from Sunkev."
          text="Milestones, product updates and announcements from our team."
        />
        <Reveal delay={0.2}>
          <Link
            to="/news"
            data-testid="news-view-all"
            className="group inline-flex shrink-0 items-center gap-2 font-heading text-sm font-bold uppercase tracking-widest text-navy"
          >
            View All Updates
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/20 transition-all duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-navy-900">
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {news.slice(0, 3).map((n, i) => (
          <Reveal key={n.title} delay={i * 0.1}>
            <article className="group flex h-full flex-col rounded-3xl border border-gray-100 bg-white p-8 shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10">
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy">
                  <n.Icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
                </span>
                <span className="rounded-full bg-gold/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gold-dark">
                  {n.tag}
                </span>
              </div>
              <p className="mt-7 text-xs font-semibold uppercase tracking-wider text-fog">{n.date}</p>
              <h3 className="mt-2 font-heading text-xl font-bold leading-snug text-ink">{n.title}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">{n.text}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export default NewsSection;
